import { stateHasSubpartK } from "encamp-shared/src/waste/rcraSectionAvailability";
import { stateAdoptedSubpartP } from "encamp-shared/src/waste/subpartPAdoption";

/** The §11d / §12 fields this strips, plus the §4 state that decides. */
type SubpartFields = {
  rcraState?: string | null;
  subpartKEntityTypes?: unknown[] | null;
  subpartKWithdrawal?: boolean | null;
  subpartPActivity?: unknown;
  subpartPWithdrawal?: boolean | null;
};

/**
 * Clear §11d / §12 values the site cannot hold, so hidden data is not silently
 * persisted through a save. Shared by the facility profile dialog and the
 * 8700-12 stepper, which previously carried identical copies.
 *
 * The two subparts are gated on different questions, deliberately:
 *
 * - **Subpart K** clears on `stateHasSubpartK`, form availability. Nothing
 *   downstream reads those columns, so "the form cannot collect it, therefore
 *   do not store it" is the whole rule.
 * - **Subpart P** clears on `stateAdoptedSubpartP`, where the program is in
 *   effect. These columns now drive the pharmaceutical biennial and
 *   generator-status exemption, so clearing them is a regulatory act rather
 *   than a form-hygiene one. Texas is the case that forces the distinction: it
 *   adopted the rule but files its 8700-12 by mail rather than through
 *   RCRAInfo, so the section stays hidden while the recorded activity stays
 *   real. Gating this on form availability would delete that activity on the
 *   next unrelated save and silently put the facility's pharmaceutical waste
 *   back on the federal report.
 *
 * Hiding the section is still driven by form availability in both cases. This
 * governs only whether an existing value survives.
 */
export function stripUnavailableSubparts<T extends SubpartFields>(input: T): T {
  if (!stateHasSubpartK(input.rcraState)) {
    input.subpartKEntityTypes = [];
    input.subpartKWithdrawal = null;
  }
  if (!stateAdoptedSubpartP(input.rcraState)) {
    input.subpartPActivity = null;
    input.subpartPWithdrawal = null;
  }
  return input;
}
